import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Grid } from '@mui/material';

import { useFormatPercent } from '@arrived/hooks';
import { Offering } from '@arrived/models';

import { PROPERTY_HASH_MAP } from '../../Property.constants';
import { PropertyDetailsSection } from '../PropertyDetailsSection';
import { PropertyDetailsTooltip } from '../PropertyDetailsTooltip';
import { RentalStatusQuickLookItem } from './PropertyDetailsRentalStatusQuickLookItem';

import { PropertyDetailsQuickLookItem } from '../quickLook';
import { isRentInfoSectionEnabled } from './utils';

interface PropertyDetailsRentInfoProps {
  offering: Offering;
}

export const PropertyDetailsRentInfo = ({ offering }: PropertyDetailsRentInfoProps) => {
  const formatPercent = useFormatPercent();
  const intl = useIntl();

  const leaseStatus = offering.properties.length && offering.properties[0]?.leaseStatus;
  const firstDividendDate = offering.projectedFirstDividendDate;
  const firstDividendYieldText = useMemo(
    () =>
      offering.projectedAnnualDividendYield
        ? intl.formatMessage(
            { id: 'per-year', defaultMessage: '{percent} / YEAR' },
            { percent: formatPercent(offering.projectedAnnualDividendYield) }
          )
        : undefined,
    [offering.projectedAnnualDividendYield]
  );

  // Do not render this section if projected fields are all null
  if (!isRentInfoSectionEnabled(offering)) return null;

  return (
    <PropertyDetailsSection
      hash={PROPERTY_HASH_MAP.rentInfo}
      title={<FormattedMessage id="property-details-rent-info-title" defaultMessage="Tenant Leasing Process" />}
      description={
        <FormattedMessage
          id="property-details-rent-info-description"
          defaultMessage={`Preparing a home for the rental market is a multi-tiered process that begins
           as soon as the property is acquired. Our experienced investment team works closely with the
           local property managers to get it show-ready, determine optimal market rent, take high-quality
            photos and videos, market the rental through various online and offline channels, screen and
             thoroughly vet tenant applications, and finalize the lease terms. Below you can find more
             information regarding the current rental status, the expected timeline, and the rental income.
      `}
        />
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
          {leaseStatus && <RentalStatusQuickLookItem leaseStatus={leaseStatus} />}
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
          <PropertyDetailsQuickLookItem
            label={
              <PropertyDetailsTooltip title={<FormattedMessage id="first.dividend.date.description" />}>
                <FormattedMessage id="first.dividend.date" />
              </PropertyDetailsTooltip>
            }
            value={firstDividendDate}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
          <PropertyDetailsQuickLookItem
            label={
              <PropertyDetailsTooltip
                title={
                  <FormattedMessage
                    id="rent-info-dividend-yield-title-tooltip"
                    defaultMessage={`The annualized dividend is the return on investment based on the first dividend
                     payment. It's calculated by dividing the first dividend payment by the raise amount and annualizing
                      that figure.`}
                  />
                }
              >
                <FormattedMessage id="rent-info-dividend-yield-title" defaultMessage="First Dividend Yield" />
              </PropertyDetailsTooltip>
            }
            value={firstDividendYieldText}
          />
        </Grid>
        <Grid item xs={12}>
          <PropertyDetailsQuickLookItem
            label={<FormattedMessage id="rent-info-timeline-title" defaultMessage="Timeline to Rent" />}
            value={
              <FormattedMessage
                id="rent-info-timeline-description"
                defaultMessage={`The time needed to sign the first tenant lease is variable and can depend
             on several factors including seasonality and whether the property requires any sort of renovation.
              Historically the average time to lease has been 45 days and has ranged from a minimum of 7 days
               to over 90 days.`}
              />
            }
            valueTypographyProps={{ variant: 'body1', textTransform: 'none' }}
          />
        </Grid>
      </Grid>
    </PropertyDetailsSection>
  );
};
